import { Box, Button } from '@mui/material';
import { TrendingUp as TrendIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../../theme/chart';
import { tokens } from '../../../theme/tokens';
import { computeBudget } from '../../../utils/budget';
import type { MonthSummary } from './dashboard.types';
import { BigNumber, Eyebrow, Panel, Tile } from './ui';

const hatch = (step: number) =>
  `repeating-linear-gradient(135deg, ${tokens.accent} 0 ${step}px, ${tokens.accentHatch} ${step}px ${step * 2}px)`;

const Swatch = ({ background }: { background: string }) => (
  <Box component='span' aria-hidden sx={{ width: 10, height: 10, borderRadius: '3px', background, flexShrink: 0 }} />
);

interface Props {
  month: string;
  summary: MonthSummary;
  incomeEntries: number;
  expenseEntries: number;
  /** expenses vs the month before, as a fraction */
  expensesDelta?: number;
  /** where spending lands on the month's last day at the current pace */
  projectedEnd: number;
  currency: string;
  onSetBudget?: () => void;
}

/**
 * The top of the personal Início: how much of the month's budget is still
 * free, the bar of spent / promised to open bills / free with today's mark,
 * the pace warning, and the month in three numbers beside it.
 */
export function BudgetHero({ month, summary, incomeEntries, expenseEntries, expensesDelta, projectedEnd, currency, onSetBudget }: Props) {
  const { t, i18n } = useTranslation('dashboard');
  const locale = i18n.language;
  const money = (value: number) => formatMoney(value, currency, locale);
  const { income, expenses, committed, budget, dayOfMonth, daysInMonth, closed } = summary;
  const breakdown = computeBudget({ spent: expenses, committed, limit: budget });
  const monthDate = new Date(`${month}-01T12:00:00`);
  const monthName = monthDate.toLocaleDateString(locale, { month: 'long' });
  const previousName = new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 15).toLocaleDateString(locale, { month: 'long' });
  const today = daysInMonth > 0 ? dayOfMonth / daysInMonth : 0;
  const over = breakdown.overBy > 0;
  const left = income - expenses;

  const deltaText =
    expensesDelta === undefined || Math.round(expensesDelta * 100) === 0
      ? null
      : t(expensesDelta > 0 ? 'home.hero.deltaUp' : 'home.hero.deltaDown', {
          pct: Math.abs(Math.round(expensesDelta * 100)),
          month: previousName,
        });

  // The pace line: only while the month runs, and only when there is a pace to read.
  const paceOver = breakdown.hasLimit && projectedEnd > budget;
  const showPace = !closed && dayOfMonth > 0 && expenses > 0;

  return (
    <Panel label={t('home.hero.label', { month: monthName })} hero sx={{ flexDirection: 'row', flexWrap: 'wrap', gap: { xs: 2.5, md: 3.5 } }}>
      <Box sx={{ flex: '2 1 440px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        <Eyebrow>{breakdown.hasLimit ? t('home.hero.label', { month: monthName }) : t('home.hero.spentLabel', { month: monthName })}</Eyebrow>

        {breakdown.hasLimit ? (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1.5, rowGap: 0.75 }}>
              <BigNumber color={over ? tokens.warn : undefined}>{money(over ? breakdown.overBy : breakdown.remaining)}</BigNumber>
              <Box component='span' sx={{ color: tokens.ink2 }}>
                {over ? t('home.hero.overOf', { budget: money(budget) }) : t('home.hero.freeOf', { budget: money(budget) })}
              </Box>
            </Box>

            <Box sx={{ position: 'relative', pt: closed ? 0 : 2.25 }}>
              {!closed && (
                <Box
                  component='span'
                  sx={{
                    position: 'absolute',
                    left: `${today * 100}%`,
                    top: 0,
                    transform: today > 0.9 ? 'translateX(-100%)' : 'translateX(-50%)',
                    fontSize: 11,
                    fontWeight: 700,
                    color: tokens.ink3,
                  }}
                >
                  {t('home.today')}
                </Box>
              )}
              <Box
                role='img'
                aria-label={t('home.hero.barLabel', { spent: money(expenses), committed: money(committed), free: money(breakdown.remaining) })}
                sx={{ display: 'flex', height: 14, borderRadius: 99, overflow: 'hidden', bgcolor: tokens.line }}
              >
                <Box sx={{ width: `${breakdown.spentPct}%`, bgcolor: over ? tokens.warn : tokens.accent }} />
                {!closed && breakdown.committedPct > 0 && <Box sx={{ width: `${breakdown.committedPct}%`, background: hatch(3) }} />}
              </Box>
              {!closed && (
                <Box aria-hidden sx={{ position: 'absolute', left: `${today * 100}%`, top: 14, width: 2, height: 22, bgcolor: tokens.ink }} />
              )}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2.25, rowGap: 0.75, fontSize: 13, color: tokens.ink2 }}>
              <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <Swatch background={over ? tokens.warn : tokens.accent} />
                {t('home.spent')} {money(expenses)}
              </Box>
              {!closed && (
                <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  <Swatch background={hatch(2)} />
                  {t('home.hero.openBills')} {money(committed)}
                </Box>
              )}
              <Box component='span' sx={{ display: { xs: 'none', sm: 'inline-flex' }, alignItems: 'center', gap: 0.75 }}>
                <Swatch background={tokens.line} />
                {t('home.free')} {money(breakdown.remaining)}
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1.5, rowGap: 0.75 }}>
              <BigNumber>{money(expenses)}</BigNumber>
              <Box component='span' sx={{ color: tokens.ink2 }}>{t('home.transactions', { count: expenseEntries })}</Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, p: 1.75, borderRadius: '14px', bgcolor: tokens.bg }}>
              <Box component='span' sx={{ flex: '1 1 220px', fontSize: 14, color: tokens.ink2 }}>{t('home.hero.noBudget')}</Box>
              {onSetBudget && (
                <Button variant='outlined' size='small' onClick={onSetBudget} sx={{ minHeight: 40 }}>
                  {t('home.hero.setBudget')}
                </Button>
              )}
            </Box>
          </>
        )}

        {showPace && (
          <Box
            sx={{
              display: 'flex',
              gap: 1.25,
              alignItems: 'flex-start',
              px: 1.75,
              py: 1.5,
              borderRadius: '14px',
              bgcolor: paceOver ? tokens.warnSoft : tokens.accentSoft,
            }}
          >
            <TrendIcon aria-hidden sx={{ fontSize: 18, mt: '2px', color: paceOver ? tokens.warn : tokens.accent, flexShrink: 0 }} />
            <Box component='span' sx={{ fontSize: 14 }}>
              {t('home.hero.paceStart')} <strong>{money(projectedEnd)}</strong>
              {breakdown.hasLimit && (
                paceOver ? (
                  <>
                    {', '}
                    <Box component='strong' sx={{ color: tokens.warn }}>
                      {t('home.hero.paceOver', { amount: money(projectedEnd - budget) })}
                    </Box>
                  </>
                ) : (
                  <>, {t('home.hero.paceWithin')}</>
                )
              )}
              .
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          flex: '1 1 240px',
          minWidth: 0,
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', md: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))' },
          gap: { xs: 1, md: 1.5 },
          alignContent: 'start',
        }}
      >
        <Tile label={t('home.income')} value={money(income)} hint={<Box component='span' sx={{ display: { xs: 'none', md: 'inline' } }}>{t('home.entries', { count: incomeEntries })}</Box>} />
        <Tile
          label={t('home.expenses')}
          value={money(expenses)}
          hint={
            <Box component='span' sx={{ display: { xs: 'none', md: 'inline' } }}>
              {t('home.transactions', { count: expenseEntries })}
              {deltaText && ` · ${deltaText}`}
            </Box>
          }
        />
        <Tile
          label={closed ? t('home.hero.leftIn', { month: monthName }) : t('home.hero.left')}
          value={money(left)}
          tone='accent'
          valueColor={left < 0 ? tokens.warn : undefined}
          hint={
            income > 0 && left > 0 ? (
              <Box component='span' sx={{ display: { xs: 'none', md: 'inline' } }}>
                {t('home.hero.ofIncome', { pct: Math.round((left / income) * 100) })}
              </Box>
            ) : undefined
          }
        />
      </Box>
    </Panel>
  );
}
